import React, { useContext, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import loadConfiguration from 'ggApp/utils/configuration';

import {
    mixLinkOnNavEnabled as mixLinkOnNavEnabledSelector,
    isB2BStoreOpenEnabled as isB2BStoreOpenEnabledSelector,
} from 'ggApp/modules/featureFlag/selectors';
import { TranslatorComponent_Text as Translator } from 'ggApp/modules/translator';
import { NormalHeadline, SmallHeadline } from 'ggApp/shared/common/Typography';

import { Wrap, Link, Background, Discount, New, Trending, B2B, B2C } from './styled';

const { APP_CONFIG } = loadConfiguration();
const { shopHost: baseUrl } = APP_CONFIG;

export const utmParams = '?utm_source=grover&utm_medium=referral';

const PageList = ({
    className,
    closeMenu,
    isBusinessStore,
    isLoggedIn,
    activeStore,
    mixLinkOnNavEnabled,
    isB2BStoreOpenEnabled,
    businessStoreDealsEnabled,
    user,
    b2cRedirectionModal,
}) => {
    const theme = useContext(ThemeContext);

    const b2cLinkOnClick = (e) => {
        e.preventDefault();
        closeMenu();
        b2cRedirectionModal.openModal();
    };

    const shouldShowRedirectionModal =
        isBusinessStore && isLoggedIn && user && user.default_store.code.includes('business');

    const { activeLanguage, code } = activeStore;
    const isBusiness = code.includes('business');

    const buildBusinessLinkProps = useCallback(() => {
        const customerStore = code === 'business' ? 'de' : code.split('_')[1];

        if (isBusiness) {
            return {
                id: 'customer-lp',
                tk: 'OFFERINGS_LINKS_B2C_TEXT',
                to: `/${customerStore}-${activeLanguage}`,
                external: true,
                target: '_self',
            };
        }

        const businessStore = code === 'de' ? 'business' : `business_${code}`;
        const hasLandingPage = code === 'de' || isB2BStoreOpenEnabled;

        const landingPageLink = `${baseUrl}/${businessStore}-${activeLanguage}/for-business`;

        return {
            id: 'business-lp',
            tk: 'OFFERINGS_LINKS_B2B_TEXT',
            to: hasLandingPage
                ? landingPageLink
                : `https://get-business.grover.com/${code}-${activeLanguage}/${utmParams}`,
            external: hasLandingPage,
            target: hasLandingPage ? '_self' : '_blank',
        };
    }, [activeLanguage, isB2BStoreOpenEnabled, code, isBusiness]);

    const linkProps = buildBusinessLinkProps();

    return (
        <Wrap className={className}>
            <Link external={false} to="/trending" onClick={closeMenu}>
                <Background />
                <Trending color={theme.colors.secondary} />
                <NormalHeadline>
                    <Translator tk="OFFERINGS_LINKS_TOP_PRODUCTS_TEXT" />
                </NormalHeadline>
            </Link>

            {(!isBusiness || businessStoreDealsEnabled) && (
                <Link external={false} to="/deals" color={theme.colors.Color1} onClick={closeMenu}>
                    <Background />
                    <Discount color={theme.colors.Color1} />
                    <SmallHeadline>
                        <Translator tk="OFFERINGS_LINKS_DEALS_TEXT" />
                    </SmallHeadline>
                </Link>
            )}

            {mixLinkOnNavEnabled && !isBusiness && activeStore.code === 'de' && (
                <Link external={false} to="/mix" color={theme.colors.Color1} onClick={closeMenu}>
                    <Background />
                    <New color={theme.colors.Color1} />
                    <SmallHeadline>
                        <Translator tk="MIX_LANDING_NAV_TITLE" />
                    </SmallHeadline>
                </Link>
            )}
            <Link
                to={linkProps.to}
                external={linkProps.external}
                onClick={shouldShowRedirectionModal ? b2cLinkOnClick : closeMenu}
                target={linkProps.target}
            >
                <Background />

                {isBusiness ? <B2C /> : <B2B />}

                <SmallHeadline>
                    <Translator tk={linkProps.tk} />
                </SmallHeadline>
            </Link>
        </Wrap>
    );
};

PageList.propTypes = {
    className: PropTypes.string,
    closeMenu: PropTypes.func,
    isBusinessStore: PropTypes.bool,
    mixLinkOnNavEnabled: PropTypes.bool,
};

PageList.defaultProps = {
    className: '',
    closeMenu: () => {},
    isBusinessStore: false,
    mixLinkOnNavEnabled: false,
};

const enhance = connect(
    createStructuredSelector({
        mixLinkOnNavEnabled: mixLinkOnNavEnabledSelector,
        isB2BStoreOpenEnabled: isB2BStoreOpenEnabledSelector,
    }),
);

export default enhance(PageList);
